/**
 * Evasion Calculations
 * Pure functions for Evasion-related mechanics (Natural Evasion, etc.)
 */

import { hasLoadoutCard } from './cardFeatureDetection';
import { hasClass, hasSubclass } from './featureDetection';

/**
 * Check if character has Natural Evasion ability (Elemental Origin Sorcerer, Level 2+)
 * @param {Object} character - Character object
 * @returns {boolean} True if character can use Natural Evasion
 */
export const hasNaturalEvasion = (character) => {
  if (!character) {
    return false;
  }

  // Must be Sorcerer with Elemental Origin subclass
  const isSorcerer = hasClass(character, 'sorcerer');
  const isElementalOrigin = hasSubclass(character, 'elemental-origin');

  // Available at Level 2+ (Specialization tier)
  const meetsLevelRequirement = (character.level || 1) >= 2;

  return isSorcerer && isElementalOrigin && meetsLevelRequirement;
};

/**
 * Get element-specific theming for Natural Evasion
 * @param {string} element - Character's chosen element (air, earth, fire, lightning, water)
 * @returns {Object} Theme configuration
 */
export const getElementalTheme = (element) => {
  const themes = {
    air: {
      icon: '💨',
      primary: 'text-cyan-400',
      glow: 'drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]',
      bg: 'bg-cyan-900/20',
      bgHover: 'bg-cyan-900/40',
      bgActive: 'bg-cyan-700',
      border: 'border-cyan-600',
      borderHover: 'border-cyan-500',
      name: 'Air'
    },
    earth: {
      icon: '🪨',
      primary: 'text-amber-400',
      glow: 'drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]',
      bg: 'bg-amber-900/20',
      bgHover: 'bg-amber-900/40',
      bgActive: 'bg-amber-700',
      border: 'border-amber-600',
      borderHover: 'border-amber-500',
      name: 'Earth'
    },
    fire: {
      icon: '🔥',
      primary: 'text-orange-400',
      glow: 'drop-shadow-[0_0_8px_rgba(251,146,60,0.6)]',
      bg: 'bg-orange-900/20',
      bgHover: 'bg-orange-900/40',
      bgActive: 'bg-orange-700',
      border: 'border-orange-600',
      borderHover: 'border-orange-500',
      name: 'Fire'
    },
    lightning: {
      icon: '⚡',
      primary: 'text-blue-400',
      glow: 'drop-shadow-[0_0_8px_rgba(96,165,250,0.6)]',
      bg: 'bg-blue-900/20',
      bgHover: 'bg-blue-900/40',
      bgActive: 'bg-blue-700',
      border: 'border-blue-600',
      borderHover: 'border-blue-500',
      name: 'Lightning'
    },
    water: {
      icon: '💧',
      primary: 'text-teal-400',
      glow: 'drop-shadow-[0_0_8px_rgba(45,212,191,0.6)]',
      bg: 'bg-teal-900/20',
      bgHover: 'bg-teal-900/40',
      bgActive: 'bg-teal-700',
      border: 'border-teal-600',
      borderHover: 'border-teal-500',
      name: 'Water'
    }
  };

  // Default to lightning if element not specified
  return themes[element?.toLowerCase()] || themes.lightning;
};

/**
 * Roll d6 for Natural Evasion bonus
 * @returns {number} d6 result (1-6)
 */
export const rollNaturalEvasionBonus = () => {
  return Math.floor(Math.random() * 6) + 1;
};

/**
 * Calculate boosted Evasion value
 * @param {number} baseEvasion - Character's base Evasion stat
 * @param {number} boost - Bonus from Natural Evasion (d6 result)
 * @returns {number} Total boosted Evasion
 */
export const calculateBoostedEvasion = (baseEvasion, boost) => {
  return (baseEvasion || 0) + (boost || 0);
};

// ============================================================================
// I SEE IT COMING (Bone L1 Domain Card)
// ============================================================================

/**
 * Check if character has I See It Coming ability (Bone domain card)
 * Card ID: core_domain_card_i_see_it_coming
 * @param {Object} character - Character object
 * @param {Object} allData - Data context (unused, kept for API compatibility)
 * @returns {boolean} True if character has the card equipped in loadout
 */
export const hasISeeItComing = (character, allData) => {
  return hasLoadoutCard(character, 'i-see-it-coming');
};

/**
 * Roll d4 for I See It Coming bonus
 * @returns {number} d4 result (1-4)
 */
export const rollISeeItComingBonus = () => {
  return Math.floor(Math.random() * 4) + 1;
};

/**
 * Get bone domain theming for I See It Coming
 * @returns {Object} Theme configuration
 */
export const getBoneTheme = () => ({
  icon: '💀',
  primary: 'text-stone-300',
  glow: 'drop-shadow-[0_0_8px_rgba(168,162,158,0.6)]',
  bg: 'bg-stone-900/20',
  bgHover: 'bg-stone-900/40',
  bgActive: 'bg-stone-700',
  border: 'border-stone-500',
  borderHover: 'border-stone-400',
  name: 'Bone'
});

// ============================================================================
// GIFTED TRACKER (Sage L1 Domain Card)
// ============================================================================

/**
 * Check if character has Gifted Tracker ability (Sage domain card)
 * Card ID: core_domain_card_gifted_tracker
 * @param {Object} character - Character object
 * @param {Object} allData - Data context (unused, kept for API compatibility)
 * @returns {boolean} True if character has the card equipped in loadout
 */
export const hasGiftedTracker = (character, allData) => {
  return hasLoadoutCard(character, 'gifted-tracker');
};

/**
 * Get tracking questions for Gifted Tracker
 * @returns {string[]} List of available questions
 */
export const getGiftedTrackerQuestions = () => [
  'What direction did they go?',
  'How long ago did they pass through?',
  'What were they doing in this location?',
  'How many of them were here?'
];

/**
 * Get sage domain theming for Gifted Tracker
 * @returns {Object} Theme configuration
 */
export const getSageTheme = () => ({
  icon: '🌿',
  primary: 'text-emerald-400',
  glow: 'drop-shadow-[0_0_8px_rgba(52,211,153,0.6)]',
  bg: 'bg-emerald-900/20',
  bgHover: 'bg-emerald-900/40',
  bgActive: 'bg-emerald-700',
  border: 'border-emerald-600',
  borderHover: 'border-emerald-500',
  name: 'Sage'
});
